import {
  ROUTE_SCORING_VERSION,
  canonicalJsonStringify,
  compareRouteUseOrder,
  normalizeRouteSeedForCurrentScoring,
  rankRouteCandidates,
  routeCopySourceSnapshotSha256,
  routeFingerprintSha256,
  routeSeedSchema,
  routeTemplateSchema,
  routeVariantSchema,
  selectPreselectedRoute,
  shiftAggregateSchema,
  shiftEditorDraftSchema,
  uuidSchema,
  type JsonValue,
  type LocationSnapshot,
  type RankedRouteCandidate,
  type RouteOfferSource,
  type RoutePreselectionResult,
  type RouteScoringCandidate,
  type RouteScoringInput,
  type RouteSeed,
  type RouteTemplate,
  type RouteVariant,
  type ShiftAggregate,
  type ShiftEditorDraft,
} from '@der-maschinfuehrer/contracts';

import {
  applyDraftRouteSeed,
  endpointToLocationSnapshot,
  routeOfferSelectionId,
  selectManualDraftRoute,
  type AlphaUuidFactory,
  type DraftRouteMutationOptions,
} from './alpha-shift';

export type NetworkRouteUnavailableReason =
  'graph_not_bundled' | 'not_release_eligible' | 'not_prepared' | 'incompatible' | 'corrupt';

export interface NetworkRouteCandidateInput {
  readonly offer_id: string;
  readonly name?: string | null;
  readonly origin: LocationSnapshot;
  readonly via: readonly LocationSnapshot[];
  readonly destination: LocationSnapshot;
  readonly route_seed: RouteSeed;
}

export type NetworkRouteAvailability =
  | { readonly status: 'loading' }
  | { readonly status: 'unavailable'; readonly reason: NetworkRouteUnavailableReason }
  | { readonly status: 'ready_no_route' }
  | {
      readonly status: 'ready';
      readonly candidates: readonly NetworkRouteCandidateInput[];
    };

export interface RouteOffer {
  readonly offer_id: string;
  readonly offer_source: RouteOfferSource;
  readonly name: string | null;
  readonly route_variant_id: string | null;
  readonly route_variant_revision: number | null;
  readonly route_template_id: string | null;
  readonly origin: LocationSnapshot;
  readonly via: readonly LocationSnapshot[];
  readonly destination: LocationSnapshot;
  readonly route_seed: RouteSeed;
  readonly favorite: boolean;
  readonly default_for_context: boolean;
  readonly explicit_confirmation_count: number;
  readonly use_count: number;
  /** Truthful occurrence date for display; confirmation/storage timestamps are not recency. */
  readonly last_used_service_date: string | null;
  readonly score: RouteFingerprintScore;
  readonly preselected: boolean;
  readonly template_defaults: RouteOfferTemplateDefaults | null;
  /** Exact source proof for a copy-context offer; never trusted without composer validation. */
  readonly route_copy_source: ShiftEditorDraft['route_copy_source'];
}

export interface RouteFingerprintScore {
  readonly route_scoring_version: RankedRouteCandidate['route_scoring_version'];
  readonly canonical_candidate_id: string;
  readonly route_fingerprint_sha256: string;
  readonly score: number;
  readonly breakdown: RankedRouteCandidate['breakdown'];
  readonly rank: number;
  /** Whether this route identity won automatic preselection; the offer itself may still be an alias. */
  readonly preselected: boolean;
}

export interface RouteOfferTemplateDefaults {
  readonly train_or_service_number: string;
  readonly work_type: ShiftEditorDraft['work_type'];
  readonly operator: string;
  readonly vehicles: ShiftEditorDraft['vehicles'];
}

export interface RouteOfferComposition {
  readonly route_scoring_version: typeof ROUTE_SCORING_VERSION;
  readonly history: readonly RouteOffer[];
  readonly templates: readonly RouteOffer[];
  readonly network: readonly RouteOffer[];
  readonly network_availability: NetworkRouteAvailability;
  readonly preselection: RoutePreselectionResult;
  readonly preselected_offer_id: string | null;
}

export interface ComposeRouteOffersInput {
  readonly draft: ShiftEditorDraft;
  readonly shifts: readonly ShiftAggregate[];
  readonly routeVariants: readonly RouteVariant[];
  readonly templates: readonly RouteTemplate[];
  readonly network: NetworkRouteAvailability;
  readonly evaluatedAt: string;
}

interface UnscoredRouteOffer extends Omit<RouteOffer, 'score' | 'preselected'> {
  readonly scoring_candidate: RouteScoringCandidate;
}

interface HistoryUse {
  readonly aggregate: ShiftAggregate;
  readonly segment: ShiftAggregate['segments'][number];
  readonly snapshot: NonNullable<ShiftAggregate['segments'][number]['route_snapshot']>;
}

interface HistoryAlias {
  readonly route_id: string;
  readonly route: RouteVariant | undefined;
  readonly exact_uses: readonly HistoryUse[];
}

export function composeRouteOffers(input: ComposeRouteOffersInput): RouteOfferComposition {
  const draft = shiftEditorDraftSchema.parse(input.draft);
  const networkAvailability = input.network;
  if (!hasCompleteDirection(draft)) {
    return emptyComposition(networkAvailability);
  }
  const destination = draft.destination;
  if (destination === null) return emptyComposition(networkAvailability);
  const originId = draft.origin.location_id;
  const destinationId = destination.location_id;
  const context = {
    origin_location_id: originId,
    destination_location_id: destinationId,
    train_or_service_number: nullableTrimmed(draft.train_or_service_number),
    work_type: draft.work_type,
    operator: nullableTrimmed(draft.operator),
    vehicle_ids: draft.vehicles.map((vehicle) => vehicle.vehicle_id),
  } satisfies RouteScoringInput['context'];

  const history = historyOffers(input.shifts, input.routeVariants, originId, destinationId, draft);
  const templates = templateOffers(input.templates, originId, destinationId);
  const network =
    networkAvailability.status === 'ready'
      ? networkOffers(networkAvailability.candidates, originId, destinationId)
      : [];
  const unscored = [...history, ...templates, ...network];
  const canonicalCandidates = canonicalScoringCandidates(unscored);
  const scoringInput: RouteScoringInput = {
    route_scoring_version: ROUTE_SCORING_VERSION,
    evaluated_at: input.evaluatedAt,
    evaluated_service_date: draft.service_date,
    context,
    candidates: canonicalCandidates.map((offer) => offer.scoring_candidate),
  };
  const ranked = rankRouteCandidates(scoringInput);
  const canonicalByFingerprint = new Map(
    canonicalCandidates.map(
      (offer) => [offer.scoring_candidate.route_fingerprint_sha256, offer] as const,
    ),
  );
  const scoreByCanonicalId = new Map(
    ranked.map((result) => [result.candidate_id, result] as const),
  );
  const preselection = selectPreselectedRoute(scoringInput);
  const preselectedOfferId = preselection.selected ? preselection.candidate_id : null;
  const scoredById = new Map(
    unscored.map((offer) => {
      const canonical = canonicalByFingerprint.get(
        offer.scoring_candidate.route_fingerprint_sha256,
      );
      const score =
        canonical === undefined
          ? undefined
          : scoreByCanonicalId.get(canonical.scoring_candidate.candidate_id);
      if (score === undefined) throw new Error('route scorer omitted a composed candidate');
      const { scoring_candidate: scoringCandidate, ...value } = offer;
      void scoringCandidate;
      const projectedScore: RouteFingerprintScore = {
        route_scoring_version: score.route_scoring_version,
        canonical_candidate_id: score.candidate_id,
        route_fingerprint_sha256: score.route_fingerprint_sha256,
        score: score.score,
        breakdown: score.breakdown,
        rank: score.rank,
        preselected: score.candidate_id === preselectedOfferId,
      };
      return [
        offer.offer_id,
        {
          ...value,
          score: projectedScore,
          preselected: offer.offer_id === preselectedOfferId,
        } satisfies RouteOffer,
      ] as const;
    }),
  );
  const projectGroup = (offers: readonly UnscoredRouteOffer[]): RouteOffer[] =>
    offers
      .map((offer) => scoredById.get(offer.offer_id))
      .filter((offer): offer is RouteOffer => offer !== undefined)
      .sort((left, right) => left.score.rank - right.score.rank);
  return {
    route_scoring_version: ROUTE_SCORING_VERSION,
    history: projectGroup(history),
    templates: projectGroup(templates),
    network: projectGroup(network),
    network_availability: networkAvailability,
    preselection,
    preselected_offer_id: preselectedOfferId,
  };
}

function canonicalScoringCandidates(offers: readonly UnscoredRouteOffer[]): UnscoredRouteOffer[] {
  const byFingerprint = new Map<string, UnscoredRouteOffer[]>();
  for (const offer of offers) {
    const fingerprint = offer.scoring_candidate.route_fingerprint_sha256;
    const aliases = byFingerprint.get(fingerprint) ?? [];
    aliases.push(offer);
    byFingerprint.set(fingerprint, aliases);
  }
  return [...byFingerprint.values()]
    .map(canonicalScoringCandidate)
    .sort((left, right) => compareAscii(left.offer_id, right.offer_id));
}

function canonicalScoringCandidate(aliases: readonly UnscoredRouteOffer[]): UnscoredRouteOffer {
  const ordered = [...aliases].sort(compareCanonicalOffer);
  const canonical = ordered[0];
  if (canonical === undefined) throw new Error('route scoring requires a canonical candidate');
  // Exact live history is identity truth. Template counters/favorites are only
  // offer-channel metadata whenever it exists. With templates alone, V1
  // aggregates their channel signals deterministically for one identity score.
  if (canonical.scoring_candidate.exact_variant_previously_confirmed) {
    return {
      ...canonical,
      scoring_candidate: {
        ...canonical.scoring_candidate,
        // A template may express an explicit preference for this same route
        // identity, but its counters must never inflate committed history.
        favorite:
          aliases.some(
            (offer) => offer.offer_source === 'template' && offer.scoring_candidate.favorite,
          ) || canonical.scoring_candidate.favorite,
      },
    };
  }
  const templates = ordered.filter((offer) => offer.offer_source === 'template');
  if (templates.length === 0) return canonical;
  const primaryTemplate = templates[0];
  if (primaryTemplate === undefined) {
    throw new Error('route scoring requires a canonical template candidate');
  }
  return {
    ...canonical,
    scoring_candidate: {
      ...canonical.scoring_candidate,
      candidate_id: primaryTemplate.offer_id,
      favorite: templates.some((offer) => offer.scoring_candidate.favorite),
      use_count: Math.max(...templates.map((offer) => offer.scoring_candidate.use_count)),
    },
  };
}

function compareCanonicalOffer(left: UnscoredRouteOffer, right: UnscoredRouteOffer): number {
  const priority = (offer: UnscoredRouteOffer): number =>
    offer.scoring_candidate.exact_variant_previously_confirmed
      ? 0
      : offer.offer_source === 'template'
        ? 1
        : 2;
  return priority(left) - priority(right) || compareAscii(left.offer_id, right.offer_id);
}

export function applyRouteOffer(
  draftInput: ShiftEditorDraft,
  offer: RouteOffer,
  uuid: AlphaUuidFactory,
  options?: DraftRouteMutationOptions,
  selectionMethod: 'automatic' | 'explicit' = 'explicit',
): ShiftEditorDraft {
  void options;
  if (selectionMethod === 'automatic') assertAutomaticRouteOfferEligibility(offer);
  const draft = shiftEditorDraftSchema.parse(draftInput);
  if (routeOfferMatchesDraft(draft, offer)) {
    const selection = {
      kind: 'offer' as const,
      offer_source: offer.offer_source,
      offer_id: offer.offer_id,
      selection_method: selectionMethod,
    };
    const routeVariantId =
      offer.offer_source === 'history'
        ? requiredHistoryRouteVariantId(offer)
        : draft.route_variant_id;
    const routeCopySource = sameRouteCopySource(draft.route_copy_source, offer.route_copy_source)
      ? draft.route_copy_source
      : null;
    if (
      draft.route_variant_id === routeVariantId &&
      draft.route_selection?.kind === 'offer' &&
      canonicalJsonStringify(draft.route_selection satisfies JsonValue) ===
        canonicalJsonStringify(selection satisfies JsonValue) &&
      sameRouteCopySource(draft.route_copy_source, routeCopySource)
    ) {
      return draftInput;
    }
    return shiftEditorDraftSchema.parse({
      ...draft,
      route_variant_id: routeVariantId,
      route_selection: selection,
      // Confirmation is bound to the exact selection action. Manual mileage
      // remains user input and must not be replaced by the offer's distance.
      route_confirmation: null,
      route_copy_source: routeCopySource,
    });
  }
  const routeVariantId =
    offer.offer_source === 'history'
      ? requiredHistoryRouteVariantId(offer)
      : uuidSchema.parse(uuid());
  const applied = applyDraftRouteSeed(draft, {
    route_variant_id: routeVariantId,
    route_selection: {
      kind: 'offer',
      offer_source: offer.offer_source,
      offer_id: offer.offer_id,
      selection_method: selectionMethod,
    },
    origin: offer.origin,
    via: offer.via,
    destination: offer.destination,
    route_seed: normalizeRouteSeedForCurrentScoring(offer.route_seed),
    route_copy_source: sameRouteCopySource(draft.route_copy_source, offer.route_copy_source)
      ? draft.route_copy_source
      : null,
  });
  if (offer.offer_source !== 'template' || offer.template_defaults === null) return applied;
  return shiftEditorDraftSchema.parse({
    ...applied,
    train_or_service_number: offer.template_defaults.train_or_service_number,
    work_type: offer.template_defaults.work_type,
    operator: offer.template_defaults.operator,
    vehicles: offer.template_defaults.vehicles,
  });
}

export function applyHistoryRouteOffer(
  draft: ShiftEditorDraft,
  offer: RouteOffer,
  uuid: AlphaUuidFactory,
  options?: DraftRouteMutationOptions,
  selectionMethod: 'automatic' | 'explicit' = 'explicit',
): ShiftEditorDraft {
  if (offer.offer_source !== 'history') throw new Error('expected a history route offer');
  return applyRouteOffer(draft, offer, uuid, options, selectionMethod);
}

export function applyTemplateRouteOffer(
  draft: ShiftEditorDraft,
  offer: RouteOffer,
  uuid: AlphaUuidFactory,
  options?: DraftRouteMutationOptions,
): ShiftEditorDraft {
  if (offer.offer_source !== 'template') throw new Error('expected a template route offer');
  return applyRouteOffer(draft, offer, uuid, options);
}

export function applyNetworkRouteOffer(
  draft: ShiftEditorDraft,
  offer: RouteOffer,
  uuid: AlphaUuidFactory,
  options?: DraftRouteMutationOptions,
): ShiftEditorDraft {
  if (offer.offer_source !== 'network') throw new Error('expected a network route offer');
  return applyRouteOffer(draft, offer, uuid, options);
}

export function applyAutomaticRouteOffer(
  draft: ShiftEditorDraft,
  offer: RouteOffer,
  uuid: AlphaUuidFactory,
  options?: DraftRouteMutationOptions,
): ShiftEditorDraft {
  assertAutomaticRouteOfferEligibility(offer);
  return applyRouteOffer(draft, offer, uuid, options, 'automatic');
}

function assertAutomaticRouteOfferEligibility(offer: RouteOffer): void {
  if (
    offer.offer_source !== 'history' ||
    offer.route_variant_id === null ||
    !offer.preselected ||
    offer.explicit_confirmation_count === 0
  ) {
    throw new Error(
      'only the preselected exact confirmed history offer can be applied automatically',
    );
  }
}

export function clearAutomaticRouteSelection(
  draftInput: ShiftEditorDraft,
  uuid: AlphaUuidFactory,
): ShiftEditorDraft {
  const draft = shiftEditorDraftSchema.parse(draftInput);
  if (
    draft.route_selection?.kind !== 'offer' ||
    draft.route_selection.selection_method !== 'automatic'
  ) {
    return draftInput;
  }
  return shiftEditorDraftSchema.parse({
    ...draft,
    route_variant_id: uuidSchema.parse(uuid()),
    route_selection: null,
    route_seed: null,
    route_confirmation: null,
    route_copy_source: null,
    distance_input_mode: 'none',
    distance_metres: null,
  });
}

export function bindRouteOfferToKnownVariant(
  draftInput: ShiftEditorDraft,
  offer: RouteOffer,
  routeInput: RouteVariant,
): ShiftEditorDraft {
  const draft = shiftEditorDraftSchema.parse(draftInput);
  const route = routeVariantSchema.parse(routeInput);
  if (!routeOfferMatchesDraft(draft, offer)) {
    throw new Error('draft does not retain the exact offered route snapshot');
  }
  if (route.metadata.tombstone !== null) {
    throw new Error('a deleted route variant cannot be bound to a route offer');
  }
  const expectedFingerprint = routeSeedFingerprint(
    offer.origin,
    offer.destination,
    offer.route_seed,
  );
  const routeFingerprint = routeVariantFingerprint(route);
  if (
    (offer.route_variant_id !== null && route.route_variant_id !== offer.route_variant_id) ||
    routeFingerprint !== expectedFingerprint ||
    (route.confirmation === null
      ? route.use_count !== 0 || route.explicit_confirmation_count !== 0
      : route.confirmation.route_fingerprint_sha256 !== routeFingerprint) ||
    route.origin_location_id !== offer.origin.location_id ||
    route.destination_location_id !== offer.destination.location_id ||
    route.candidate_source !== offer.route_seed.candidate_source ||
    canonicalJsonStringify(route.via_location_ids satisfies JsonValue) !==
      canonicalJsonStringify(
        offer.via.map((location) => location.location_id) satisfies JsonValue,
      ) ||
    canonicalJsonStringify(route.network_segments satisfies JsonValue) !==
      canonicalJsonStringify(offer.route_seed.network_segments satisfies JsonValue) ||
    route.graph_version?.sha256 !== offer.route_seed.graph_version?.sha256
  ) {
    throw new Error('known route variant does not match the exact offered route identity');
  }
  const selection = {
    kind: 'offer' as const,
    offer_source: offer.offer_source,
    offer_id: offer.offer_id,
    selection_method:
      draft.route_selection?.kind === 'offer'
        ? draft.route_selection.selection_method
        : ('explicit' as const),
  };
  return shiftEditorDraftSchema.parse({
    ...draft,
    route_variant_id: route.route_variant_id,
    route_selection: selection,
    route_confirmation:
      draft.route_confirmation === null ? null : { ...draft.route_confirmation, selection },
    route_copy_source: sameRouteCopySource(draft.route_copy_source, offer.route_copy_source)
      ? draft.route_copy_source
      : null,
  });
}

export function routeVariantMatchesRouteOffer(
  offer: RouteOffer,
  routeInput: RouteVariant,
): boolean {
  try {
    const route = routeVariantSchema.parse(routeInput);
    if (route.metadata.tombstone !== null) return false;
    const expectedFingerprint = routeSeedFingerprint(
      offer.origin,
      offer.destination,
      offer.route_seed,
    );
    const fingerprint = routeVariantFingerprint(route);
    return (
      (offer.route_variant_id === null || route.route_variant_id === offer.route_variant_id) &&
      fingerprint === expectedFingerprint &&
      (route.confirmation === null
        ? route.use_count === 0 && route.explicit_confirmation_count === 0
        : route.confirmation.route_fingerprint_sha256 === fingerprint) &&
      route.origin_location_id === offer.origin.location_id &&
      route.destination_location_id === offer.destination.location_id &&
      route.candidate_source === offer.route_seed.candidate_source &&
      canonicalJsonStringify(route.via_location_ids satisfies JsonValue) ===
        canonicalJsonStringify(
          offer.via.map((location) => location.location_id) satisfies JsonValue,
        ) &&
      canonicalJsonStringify(route.network_segments satisfies JsonValue) ===
        canonicalJsonStringify(offer.route_seed.network_segments satisfies JsonValue) &&
      route.graph_version?.sha256 === offer.route_seed.graph_version?.sha256
    );
  } catch {
    return false;
  }
}

export function applyManualRoute(
  draft: ShiftEditorDraft,
  uuid: AlphaUuidFactory,
  options: DraftRouteMutationOptions,
): ShiftEditorDraft {
  return selectManualDraftRoute(draft, uuid, options);
}

export function routeOfferMatchesDraft(draftInput: ShiftEditorDraft, offer: RouteOffer): boolean {
  const parsed = shiftEditorDraftSchema.safeParse(draftInput);
  if (!parsed.success) return false;
  const draft = parsed.data;
  if (
    draft.route_selection?.kind !== 'offer' ||
    !sameEndpointSnapshot(draft.origin, offer.origin) ||
    draft.destination === null ||
    !sameEndpointSnapshot(draft.destination, offer.destination) ||
    draft.via.length !== offer.via.length ||
    draft.via.some((endpoint, index) => {
      const location = offer.via[index];
      return location === undefined || !sameEndpointSnapshot(endpoint, location);
    }) ||
    canonicalJsonStringify(draft.route_seed satisfies JsonValue) !==
      canonicalJsonStringify(offer.route_seed satisfies JsonValue)
  ) {
    return false;
  }
  const exactIdentity =
    draft.route_selection.offer_source === offer.offer_source &&
    draft.route_selection.offer_id === offer.offer_id &&
    (offer.route_variant_id === null || draft.route_variant_id === offer.route_variant_id);
  if (exactIdentity) return true;
  const source = draft.route_copy_source;
  if (
    source === null ||
    offer.offer_source !== 'history' ||
    offer.route_variant_id === null ||
    draft.route_selection.offer_source !== 'history' ||
    draft.route_selection.selection_method !== 'explicit' ||
    !sameRouteCopySource(source, offer.route_copy_source)
  ) {
    return false;
  }
  return (
    draft.route_variant_id === source.source_route_variant_id &&
    draft.route_selection.offer_id ===
      routeOfferSelectionId('history', source.source_route_variant_id)
  );
}

export function manualRouteSelected(draftInput: ShiftEditorDraft): boolean {
  const parsed = shiftEditorDraftSchema.safeParse(draftInput);
  return parsed.success && parsed.data.route_selection?.kind === 'manual';
}

export function selectedRouteOfferId(draftInput: ShiftEditorDraft): string | null {
  const parsed = shiftEditorDraftSchema.safeParse(draftInput);
  return parsed.success && parsed.data.route_selection?.kind === 'offer'
    ? parsed.data.route_selection.offer_id
    : null;
}

export function routeOfferFavoriteMutation(
  offer: RouteOffer,
  favorite: boolean,
): {
  readonly route_variant_id: string;
  readonly favorite: boolean;
  readonly expected_revision: number;
} {
  if (offer.route_variant_id === null || offer.route_variant_revision === null) {
    throw new Error('only a persisted live route variant can change favorite state');
  }
  return {
    route_variant_id: offer.route_variant_id,
    favorite,
    expected_revision: offer.route_variant_revision,
  };
}

function historyOffers(
  shiftInputs: readonly ShiftAggregate[],
  routeInputs: readonly RouteVariant[],
  originId: string,
  destinationId: string,
  draft: ShiftEditorDraft,
): UnscoredRouteOffer[] {
  const parsedRoutes = routeInputs.map((route) => routeVariantSchema.parse(route));
  const tombstonedRouteIds = new Set(
    parsedRoutes
      .filter((route) => route.metadata.tombstone !== null)
      .map((route) => route.route_variant_id),
  );
  const liveRoutes = new Map(
    parsedRoutes
      .filter((route) => route.metadata.tombstone === null)
      .map((route) => [route.route_variant_id, route] as const),
  );
  const usesByRoute = new Map<string, HistoryUse[]>();
  for (const aggregateInput of shiftInputs) {
    const aggregate = shiftAggregateSchema.parse(aggregateInput);
    if (aggregate.shift.metadata.tombstone !== null) continue;
    for (const segment of aggregate.segments) {
      const snapshot = segment.route_snapshot;
      if (
        segment.metadata.tombstone !== null ||
        snapshot === null ||
        (segment.status !== 'complete' && segment.status !== 'partial') ||
        snapshot.origin.location_id !== originId ||
        snapshot.destination.location_id !== destinationId
      ) {
        continue;
      }
      const uses = usesByRoute.get(snapshot.route_variant_id) ?? [];
      uses.push({ aggregate, segment, snapshot });
      usesByRoute.set(snapshot.route_variant_id, uses);
    }
  }

  const aliasesByFingerprint = new Map<string, HistoryAlias[]>();
  for (const [routeId, uses] of usesByRoute) {
    const route = liveRoutes.get(routeId);
    if (route === undefined && tombstonedRouteIds.has(routeId)) continue;
    const latest = [...uses].sort(compareHistoryUse).at(-1);
    if (
      latest === undefined ||
      (route !== undefined && !routeMatchesSnapshot(route, latest.snapshot))
    ) {
      continue;
    }
    const exactUses = uses.filter(
      (use) =>
        use.snapshot.confirmation.route_fingerprint_sha256 ===
        latest.snapshot.confirmation.route_fingerprint_sha256,
    );
    if (exactUses.length === 0) continue;
    const fingerprint = latest.snapshot.confirmation.route_fingerprint_sha256;
    const aliases = aliasesByFingerprint.get(fingerprint) ?? [];
    aliases.push({ route_id: routeId, route, exact_uses: exactUses });
    aliasesByFingerprint.set(fingerprint, aliases);
  }

  const selectedPersistedRouteId =
    draft.route_selection?.kind === 'offer' &&
    draft.route_selection.offer_source === 'history' &&
    draft.route_selection.selection_method === 'explicit' &&
    draft.route_selection.offer_id === routeOfferSelectionId('history', draft.route_variant_id)
      ? draft.route_variant_id
      : null;
  const routeCopySource = draft.route_copy_source;
  const offers: UnscoredRouteOffer[] = [];
  for (const [fingerprint, aliases] of aliasesByFingerprint) {
    // An editor opened from an exact current persisted use must keep that use's
    // identity visible, even when deployed legacy data contain same-fingerprint
    // aliases. Outside that proven context, prefer a materialized identity so an
    // aggregate-only alias never creates a fresh duplicate. UUID order remains
    // the stable canonical tie-break.
    const contextualAlias =
      selectedPersistedRouteId === null
        ? undefined
        : aliases.find((alias) => alias.route_id === selectedPersistedRouteId);
    const contextualUse = contextualAlias?.exact_uses.find(
      (use) =>
        use.aggregate.shift.shift_id === draft.shift_id &&
        use.aggregate.aggregate_revision === draft.base_aggregate_revision &&
        use.segment.trip_segment_id === draft.trip_segment_id,
    );
    const materialized = aliases.filter((alias) => alias.route !== undefined);
    const canonical =
      contextualUse === undefined
        ? [...(materialized.length > 0 ? materialized : aliases)].sort((left, right) =>
            compareAscii(left.route_id, right.route_id),
          )[0]
        : contextualAlias;
    if (canonical === undefined) throw new Error('history aliases require a canonical identity');
    const exactUses = aliases.flatMap((alias) => alias.exact_uses);
    const copiedUseCandidate =
      routeCopySource === null
        ? undefined
        : aliases
            .find((alias) => alias.route_id === routeCopySource.source_route_variant_id)
            ?.exact_uses.find(
              (use) =>
                use.aggregate.shift.shift_id === routeCopySource.source_shift_id &&
                use.segment.trip_segment_id === routeCopySource.source_trip_segment_id,
            );
    const copiedUse =
      copiedUseCandidate !== undefined &&
      copiedDraftMatchesHistoryUse(draft, copiedUseCandidate, canonical.route_id)
        ? copiedUseCandidate
        : undefined;
    const actualLatest = [...exactUses].sort(compareHistoryUse).at(-1);
    const seedUse = contextualUse ?? copiedUse ?? actualLatest;
    if (actualLatest === undefined || seedUse === undefined) {
      throw new Error('history aliases require at least one exact use');
    }
    const routeId = canonical.route_id;
    const route = canonical.route;
    // Preferences belong to the canonical mutable UUID. Folding preferences
    // from read-only aliases would create a value the favorite CAS cannot clear.
    const favorite = route?.favorite ?? false;
    const defaultForContext = route?.default_for_context ?? false;
    const offerId = routeOfferSelectionId('history', routeId);
    const routeSeed = normalizeRouteSeedForCurrentScoring(
      routeSeedFromHistorySnapshot(seedUse.snapshot),
    );
    const observations = exactUses.map(({ aggregate, segment, snapshot }) => ({
      service_date: aggregate.shift.service_date,
      actual_start: segment.actual_start?.instant ?? null,
      origin_location_id: snapshot.origin.location_id,
      destination_location_id: snapshot.destination.location_id,
      train_or_service_number: segment.train_or_service_number,
      work_type: segment.work_type,
      operator: segment.operator,
      vehicle_ids: segment.vehicles.map((vehicle) => vehicle.vehicle_id),
    }));
    const scoringCandidate: RouteScoringCandidate = {
      candidate_id: offerId,
      route_variant_id: routeId,
      route_fingerprint_sha256: fingerprint,
      candidate_source: seedUse.snapshot.candidate_source,
      offer_source: 'history',
      origin_location_id: originId,
      destination_location_id: destinationId,
      explicit_confirmation_count: exactUses.length,
      use_count: exactUses.length,
      favorite,
      default_for_context: defaultForContext,
      exact_variant_previously_confirmed: true,
      observations,
    };
    offers.push({
      offer_id: offerId,
      offer_source: 'history',
      name: null,
      route_variant_id: routeId,
      route_variant_revision: route?.metadata.revision ?? null,
      route_template_id: null,
      origin: seedUse.snapshot.origin,
      via: seedUse.snapshot.via,
      destination: seedUse.snapshot.destination,
      route_seed: routeSeed,
      favorite,
      default_for_context: defaultForContext,
      explicit_confirmation_count: exactUses.length,
      use_count: exactUses.length,
      last_used_service_date: actualLatest.aggregate.shift.service_date,
      template_defaults: null,
      route_copy_source: copiedUse === undefined ? null : routeCopySource,
      scoring_candidate: scoringCandidate,
    });
  }
  return offers.sort((left, right) => compareAscii(left.offer_id, right.offer_id));
}

function templateOffers(
  templateInputs: readonly RouteTemplate[],
  originId: string,
  destinationId: string,
): UnscoredRouteOffer[] {
  const offers: UnscoredRouteOffer[] = [];
  for (const input of templateInputs) {
    const template = routeTemplateSchema.parse(input);
    const snapshot = template.source_snapshot;
    if (
      template.metadata.tombstone !== null ||
      snapshot === undefined ||
      snapshot.origin.location_id !== originId ||
      snapshot.destination.location_id !== destinationId
    ) {
      continue;
    }
    const offerId = routeOfferSelectionId('template', template.route_template_id);
    const routeSeed = normalizeRouteSeedForCurrentScoring(
      routeSeedSchema.parse(snapshot.route_seed),
    );
    const fingerprint = routeSeedFingerprint(snapshot.origin, snapshot.destination, routeSeed);
    const vehicleById = new Map(
      snapshot.vehicles.map((vehicle) => [vehicle.vehicle_id, vehicle] as const),
    );
    const defaults: RouteOfferTemplateDefaults = {
      train_or_service_number: template.default_train_or_service_number ?? '',
      work_type: templateWorkType(template.default_work_type),
      operator: template.default_operator ?? '',
      vehicles: template.default_vehicle_ids.map((vehicleId) => {
        const vehicle = vehicleById.get(vehicleId);
        if (vehicle === undefined) throw new Error('template vehicle snapshot is incomplete');
        return {
          vehicle_id: vehicle.vehicle_id,
          series: vehicle.series,
          full_number: vehicle.full_number ?? '',
          nickname: vehicle.nickname ?? '',
          operator: vehicle.operator ?? '',
          vehicle_type: vehicle.vehicle_type,
        };
      }),
    };
    offers.push({
      offer_id: offerId,
      offer_source: 'template',
      name: template.name,
      route_variant_id: null,
      route_variant_revision: null,
      route_template_id: template.route_template_id,
      origin: snapshot.origin,
      via: routeSeed.via,
      destination: snapshot.destination,
      route_seed: routeSeed,
      favorite: template.favorite,
      default_for_context: false,
      explicit_confirmation_count: 0,
      use_count: template.use_count,
      last_used_service_date: null,
      template_defaults: defaults,
      route_copy_source: null,
      scoring_candidate: {
        candidate_id: offerId,
        route_variant_id: null,
        route_fingerprint_sha256: fingerprint,
        candidate_source: routeSeed.candidate_source,
        offer_source: 'template',
        origin_location_id: originId,
        destination_location_id: destinationId,
        explicit_confirmation_count: 0,
        use_count: template.use_count,
        favorite: template.favorite,
        default_for_context: false,
        exact_variant_previously_confirmed: false,
        observations: [],
      },
    });
  }
  return offers.sort((left, right) => compareAscii(left.offer_id, right.offer_id));
}

function networkOffers(
  inputs: readonly NetworkRouteCandidateInput[],
  originId: string,
  destinationId: string,
): UnscoredRouteOffer[] {
  const offers: UnscoredRouteOffer[] = [];
  for (const input of inputs) {
    const routeSeed = normalizeRouteSeedForCurrentScoring(routeSeedSchema.parse(input.route_seed));
    if (input.origin.location_id !== originId || input.destination.location_id !== destinationId) {
      continue;
    }
    if (routeSeed.candidate_source !== 'network') {
      throw new Error('network offer must retain network as its root candidate source');
    }
    const networkLength = routeSeed.network_segments.reduce(
      (sum, segment) => sum + segment.length_metres,
      0,
    );
    if (
      routeSeed.network_segments.length === 0 ||
      routeSeed.graph_version === null ||
      routeSeed.distance.provenance !== 'network_graph' ||
      routeSeed.distance.quality !== 'estimated' ||
      routeSeed.distance.calculation_basis !== 'graph_estimate' ||
      routeSeed.distance.metres !== networkLength ||
      new Set(routeSeed.network_segments.map((segment) => segment.graph_edge_id)).size !==
        routeSeed.network_segments.length
    ) {
      throw new Error('network offer must retain a non-cyclic edge path and its exact graph sum');
    }
    if (
      input.via.length !== routeSeed.via.length ||
      input.via.some((location, index) => {
        const seeded = routeSeed.via[index];
        return (
          seeded === undefined ||
          canonicalJsonStringify(location satisfies JsonValue) !==
            canonicalJsonStringify(seeded satisfies JsonValue)
        );
      })
    ) {
      throw new Error('network offer via snapshots must exactly match its route seed');
    }
    const offerId = routeOfferSelectionId('network', input.offer_id);
    const fingerprint = routeSeedFingerprint(input.origin, input.destination, routeSeed);
    offers.push({
      offer_id: offerId,
      offer_source: 'network',
      name: input.name ?? null,
      route_variant_id: null,
      route_variant_revision: null,
      route_template_id: null,
      origin: input.origin,
      via: input.via,
      destination: input.destination,
      route_seed: routeSeed,
      favorite: false,
      default_for_context: false,
      explicit_confirmation_count: 0,
      use_count: 0,
      last_used_service_date: null,
      template_defaults: null,
      route_copy_source: null,
      scoring_candidate: {
        candidate_id: offerId,
        route_variant_id: null,
        route_fingerprint_sha256: fingerprint,
        candidate_source: routeSeed.candidate_source,
        offer_source: 'network',
        origin_location_id: originId,
        destination_location_id: destinationId,
        explicit_confirmation_count: 0,
        use_count: 0,
        favorite: false,
        default_for_context: false,
        exact_variant_previously_confirmed: false,
        observations: [],
      },
    });
  }
  return offers.sort((left, right) => compareAscii(left.offer_id, right.offer_id));
}

function routeSeedFromHistorySnapshot(
  snapshot: NonNullable<ShiftAggregate['segments'][number]['route_snapshot']>,
): RouteSeed {
  return routeSeedSchema.parse({
    candidate_source: snapshot.candidate_source,
    via: snapshot.via,
    network_segments: snapshot.network_segments,
    distance: snapshot.distance,
    catalog_version: snapshot.catalog_version,
    graph_version: snapshot.graph_version,
    route_scoring_version: snapshot.route_scoring_version,
  });
}

function routeSeedFingerprint(
  origin: LocationSnapshot,
  destination: LocationSnapshot,
  routeSeed: RouteSeed,
): string {
  return routeFingerprintSha256({
    candidate_source: routeSeed.candidate_source,
    destination_location_id: destination.location_id,
    graph_sha256: routeSeed.graph_version?.sha256 ?? null,
    network_edge_ids: routeSeed.network_segments.map((segment) => segment.graph_edge_id),
    origin_location_id: origin.location_id,
    via_location_ids: routeSeed.via.map((location) => location.location_id),
  });
}

function routeVariantFingerprint(route: RouteVariant): string {
  return routeFingerprintSha256({
    candidate_source: route.candidate_source,
    destination_location_id: route.destination_location_id,
    graph_sha256: route.graph_version?.sha256 ?? null,
    network_edge_ids: route.network_segments.map((segment) => segment.graph_edge_id),
    origin_location_id: route.origin_location_id,
    via_location_ids: route.via_location_ids,
  });
}

function routeMatchesSnapshot(
  route: RouteVariant,
  snapshot: NonNullable<ShiftAggregate['segments'][number]['route_snapshot']>,
): boolean {
  return (
    route.route_variant_id === snapshot.route_variant_id &&
    route.origin_location_id === snapshot.origin.location_id &&
    route.destination_location_id === snapshot.destination.location_id &&
    route.candidate_source === snapshot.candidate_source &&
    route.confirmation?.route_fingerprint_sha256 ===
      snapshot.confirmation.route_fingerprint_sha256 &&
    canonicalJsonStringify(route.via_location_ids satisfies JsonValue) ===
      canonicalJsonStringify(
        snapshot.via.map((location) => location.location_id) satisfies JsonValue,
      )
  );
}

function compareHistoryUse(left: HistoryUse, right: HistoryUse): number {
  return compareRouteUseOrder(
    {
      service_date: left.aggregate.shift.service_date,
      actual_start: left.segment.actual_start?.instant ?? null,
      shift_id: left.aggregate.shift.shift_id,
      segment_order: left.segment.order,
      trip_segment_id: left.segment.trip_segment_id,
    },
    {
      service_date: right.aggregate.shift.service_date,
      actual_start: right.segment.actual_start?.instant ?? null,
      shift_id: right.aggregate.shift.shift_id,
      segment_order: right.segment.order,
      trip_segment_id: right.segment.trip_segment_id,
    },
  );
}

function sameRouteCopySource(
  left: ShiftEditorDraft['route_copy_source'],
  right: ShiftEditorDraft['route_copy_source'],
): boolean {
  return (
    canonicalJsonStringify(left satisfies JsonValue) ===
    canonicalJsonStringify(right satisfies JsonValue)
  );
}

function copiedDraftMatchesHistoryUse(
  draft: ShiftEditorDraft,
  use: HistoryUse,
  canonicalRouteId: string,
): boolean {
  const source = draft.route_copy_source;
  const selection = draft.route_selection;
  if (
    source === null ||
    selection?.kind !== 'offer' ||
    selection.offer_source !== 'history' ||
    selection.selection_method !== 'explicit' ||
    use.aggregate.shift.shift_id !== source.source_shift_id ||
    use.segment.trip_segment_id !== source.source_trip_segment_id ||
    use.segment.metadata.revision !== source.source_trip_segment_revision ||
    use.snapshot.route_variant_id !== source.source_route_variant_id ||
    routeCopySourceSnapshotSha256(use.snapshot) !== source.source_route_snapshot_sha256 ||
    use.aggregate.segments.some((segment) => segment.trip_segment_id === draft.trip_segment_id)
  ) {
    return false;
  }
  const sourceSelectionId = routeOfferSelectionId('history', source.source_route_variant_id);
  const canonicalSelectionId = routeOfferSelectionId('history', canonicalRouteId);
  const selectionIsSource =
    draft.route_variant_id === source.source_route_variant_id &&
    selection.offer_id === sourceSelectionId;
  const selectionIsCanonical =
    draft.route_variant_id === canonicalRouteId && selection.offer_id === canonicalSelectionId;
  if (!selectionIsSource && !selectionIsCanonical) return false;
  if (
    !sameEndpointSnapshot(draft.origin, use.snapshot.origin) ||
    draft.destination === null ||
    !sameEndpointSnapshot(draft.destination, use.snapshot.destination) ||
    draft.via.length !== use.snapshot.via.length ||
    draft.via.some((endpoint, index) => {
      const snapshot = use.snapshot.via[index];
      return snapshot === undefined || !sameEndpointSnapshot(endpoint, snapshot);
    })
  ) {
    return false;
  }
  const copiedSeed = normalizeRouteSeedForCurrentScoring(
    routeSeedFromHistorySnapshot(use.snapshot),
  );
  return (
    canonicalJsonStringify(draft.route_seed satisfies JsonValue) ===
    canonicalJsonStringify(copiedSeed satisfies JsonValue)
  );
}

function requiredHistoryRouteVariantId(offer: RouteOffer): string {
  if (offer.route_variant_id === null) {
    throw new Error('history route offer requires a durable route variant identity');
  }
  return offer.route_variant_id;
}

function hasCompleteDirection(draft: ShiftEditorDraft): boolean {
  if (draft.origin.name.trim() === '' || draft.destination?.name.trim() === '') return false;
  try {
    endpointToLocationSnapshot(draft.origin);
    if (draft.destination === null) return false;
    endpointToLocationSnapshot(draft.destination);
    return true;
  } catch {
    return false;
  }
}

function sameEndpointSnapshot(
  endpoint: ShiftEditorDraft['origin'],
  snapshot: LocationSnapshot,
): boolean {
  try {
    return (
      canonicalJsonStringify(endpointToLocationSnapshot(endpoint) satisfies JsonValue) ===
      canonicalJsonStringify(snapshot satisfies JsonValue)
    );
  } catch {
    return false;
  }
}

function emptyComposition(network: NetworkRouteAvailability): RouteOfferComposition {
  const preselection = selectPreselectedRoute({
    route_scoring_version: ROUTE_SCORING_VERSION,
    evaluated_at: '1970-01-01T00:00:00.000Z',
    evaluated_service_date: '1970-01-01',
    context: {
      origin_location_id: '00000000-0000-4000-8000-000000000001',
      destination_location_id: '00000000-0000-4000-8000-000000000001',
      train_or_service_number: null,
      work_type: null,
      operator: null,
      vehicle_ids: [],
    },
    candidates: [],
  });
  return {
    route_scoring_version: ROUTE_SCORING_VERSION,
    history: [],
    templates: [],
    network: [],
    network_availability: network,
    preselection,
    preselected_offer_id: null,
  };
}

function templateWorkType(value: string | null): ShiftEditorDraft['work_type'] {
  switch (value) {
    case 'passenger':
    case 'freight':
    case 'shunting':
    case 'light_engine':
    case 'service_run':
    case 'other':
      return value;
    case null:
    default:
      return null;
  }
}

function nullableTrimmed(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

function compareAscii(left: string, right: string): number {
  return left < right ? -1 : left > right ? 1 : 0;
}
